import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search } from '@mui/icons-material';
import SidebarNav from "../components/documentation/SidebarNav";
import ApiSection from "../components/documentation/ApiSection";
import { ENDPOINTS, BASE_URLS, STANDARD_HEADERS, getAuthUrl, getPaymentUrl, getCliPaymentUrl, getPaymentRequestByIdUrl, getAccountsBaseUrl, ACCOUNTS_PATHS } from "../data/apiConfig";

const Documentation = (callback, deps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionParam = searchParams.get('section');
    const q = (searchParams.get('q') || '').trim();
    const [activeSection, setActiveSection] = useState(sectionParam || 'introduction');
    const [localQuery, setLocalQuery] = useState(q);

    // Mettre à jour l'URL quand la section change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSectionChange = useCallback((sectionId) => {
        setActiveSection(sectionId);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('section', sectionId);
        if (newParams.get('q')) newParams.delete('q'); // Supprimer q si on change de section
        setSearchParams(newParams, { replace: true });
    });

    const documentationSections = useMemo(() => [
        {
            id: 'introduction',
            title: 'Commencer',
            content: {
                description: `1. Creer un compte Netikash
                  a. Rendez-vous sur https://gateway.netikash.com/login
                  b. Connectez-vous ou creer un compte.
                  c. Accedez a votre espace developpeur, selectionner une organisation, puis le role developpeur si vous n'avez pas le role developpeur continuer entant que admin et aller dans l'onglet gestion d'utilisateur et role et ajoutez vous comme developpeur, puis reconnectez-vous.
                 2. Obtenir les identifiants API
                 a. Allez dans Cles et Application
                 b. Cliquez sur creer une nouvelle application
                 c. Apres creation de l'application remarquer sur la liste des applications votre nouvelle application. Reperer les colonnes client_id et client_secret, ses informations sont necessaires pour appeler les API Netikash`,
                access: {
                    title: '',
                    description: ``,
                    warning: '**Notez bien** Ne partagez vos clés avec personne.'
                }
            }
        },
        {
            id: 'authorization',
            title: 'Autorisation',
            content: {
                description: `Netikash utilise OAuth2 (client credentials) pour obtenir un jeton d'acces (access_token).`,

                baseUrls: {
                    production: BASE_URLS.accounts.production
                },

                endpoints: [
                    {
                        method: ENDPOINTS.auth.method,
                        path: ENDPOINTS.auth.path,
                        description: ENDPOINTS.auth.description
                    }
                ],
                headers: STANDARD_HEADERS.auth.map(h => ({
                    name: h.name,
                    type: h.value,
                    required: h.required ? 'oui' : 'non',
                    description: h.description
                })),
                requestBody: [
                    { name: 'grant_type', type: 'client_credentials', required: 'oui', description: 'Flux OAuth2 serveur-à-serveur' }
                ],
                responseBody: [
                    { name: 'access_token', type: 'string', required: 'oui', description: 'Token d\'accès' },
                    { name: 'token_type', type: 'bearer', required: 'oui', description: 'Type du token' },
                    { name: 'expires_in', type: 'number', required: 'oui', description: 'Durée de validité (en secondes)' }
                ],
                examples: {
                    curl: `curl -X POST "${getAuthUrl()}" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Basic base64(client_id:client_secret)" \\\n  -d '{"grant_type":"client_credentials"}'`
                }
            }
        },
        {
            id: 'identifiants-comptes',
            title: 'Obtenir les identifiants (service Comptes)',
            content: {
                description: `Pour intégrer les paiements Netikash, vous aurez parfois besoin d’identifiants (ex: solution_id, receiver_id) gérés par le service Comptes (OAuth2). Utilisez les API exposées ci-dessous avec la même base URL que pour l'authentification (accounts).`,
                baseUrls: { production: getAccountsBaseUrl() },
                identifiantsClarification: {
                    receiver_id: `receiver_id = identifiant du destinataire des fonds. Selon votre cas : (1) Pour recevoir sur votre organisation : utilisez l'id de votre organisation (format C-xxxCMP), obtenu via GET .../companies/show/{id} ou depuis votre back-office. (2) Pour un destinataire particulier : utilisez un user id Netikash (obtenu lors du linking/onboarding de l'utilisateur). Ne confondez pas avec le client_id OAuth (token JWT).`,
                    sender_id: `sender_id = identifiant du payeur (titulaire du moyen de paiement). Si vos utilisateurs n'ont pas de compte Netikash : utilisez "anonymous". Le numéro mobile money est fourni dans method.account. Si le payeur est un utilisateur Netikash connu : utilisez son user id (GET .../users/{user_id}/show).`
                },
                endpoints: [
                    { method: 'GET', path: ACCOUNTS_PATHS.solutions, description: 'Liste des solutions. Utilisez le champ id (ou code) comme solution_id dans vos demandes de paiement.' },
                    { method: 'GET', path: '/exposed/v1/usr/solutions/{id}', description: 'Détail d\'une solution par id.' },
                    { method: 'GET', path: '/exposed/v1/usr/solutions?code=XXX', description: 'Détail d\'une solution par code.' },
                    { method: 'GET', path: '/exposed/v1/usr/companies/{id}', description: 'Détail d\'une organisation (company).' },
                    { method: 'GET', path: '/exposed/v1/usr/companies/show/{id}', description: 'Résumé d\'une organisation avec available_solutions_ids. Utilisez le champ id comme receiver_id pour recevoir les fonds sur votre organisation.' },
                    { method: 'GET', path: '/exposed/v1/usr/companies/{org}/is-on-solution/{sol}', description: 'Vérifier qu\'une organisation est bien inscrite sur une solution (retourne true/false).' },
                    { method: 'GET', path: '/exposed/v1/usr/services?org={company_id}&sol={solution_id}', description: 'Récupérer le service (lien organisation + solution). L\'id retourné peut être utilisé comme champ service (optionnel) dans la demande de paiement.' },
                    { method: 'GET', path: '/exposed/v1/usr/services/{id}', description: 'Détail d\'un service par id.' },
                    { method: 'GET', path: '/exposed/v1/usr/users/{user_id}/show', description: 'Profil utilisateur. Pour receiver_id/sender_id si le destinataire ou payeur est un utilisateur Netikash identifié.' },
                    { method: 'GET', path: '/exposed/v1/usr/users?ids=id1,id2', description: 'Plusieurs utilisateurs par ids.' }
                ],
                identifiantsMapping: [
                    { champ: 'solution_id', source: 'GET /exposed/v1/usr/solutions ou /solutions/{id}. Champ id ou code de la solution (ex. Netikash Gateway).', champReponse: 'id ou code' },
                    { champ: 'receiver_id', source: 'Pour votre organisation : id de l\'org (C-xxxCMP) via GET .../companies/show/{id}. Pour un particulier : user id du linking.', champReponse: 'id (string)' },
                    { champ: 'sender_id', source: 'Payeur sans compte Netikash : "anonymous". Payeur identifié : user id (GET .../users/{user_id}/show).', champReponse: '"anonymous" ou user id' },
                    { champ: 'service', source: 'Optionnel. GET .../services?org={company_id}&sol={solution_id}', champReponse: 'id' }
                ]
            }
        },
        {
            id: 'payment-request',
            title: 'Demande de Paiement',
            content: {
                description: `Créez une demande de paiement via l'appel \`cli-payments\`. Utilisez ensuite \`link\` pour rediriger votre client et vérifiez le statut via \`GET /transactions/requests/{id}\`.`,
                baseUrls: { production: BASE_URLS.api.production },
                paymentRequest: {
                    fluxA: {
                        title: 'Paiement (cli-payments)',
                        subtitle: 'Cas « redirection client » : vous créez une demande et redirigez l\'utilisateur vers le lien retourné.',
                        method: ENDPOINTS.cliPayment.method,
                        path: ENDPOINTS.cliPayment.path,
                        requestBody: [
                            { name: 'amount', type: 'double', required: 'oui', description: 'Le montant à payer' },
                            { name: 'ref', type: 'string', required: 'oui', description: 'La référence du paiement (facture/commande/...)' },
                            { name: 'currency', type: 'string', required: 'oui', description: 'Devise (USD ou CDF en majuscules)' },
                            { name: 'referer_url', type: 'string', required: 'oui', description: 'URL de redirection après paiement' },
                            { name: 'label', type: 'string', required: 'non', description: 'Libellé optionnel' }
                        ],
                        responseBody: [
                            { name: 'error', type: 'boolean', required: 'oui', description: 'Indique si la requête a échoué' },
                            { name: 'message', type: 'string', required: 'non', description: 'Message de statut' },
                            { name: 'expires_at', type: 'date', required: 'oui', description: 'Date d\'expiration de la demande' },
                            { name: 'trans', type: 'string', required: 'oui', description: 'Code de la demande' },
                            { name: 'status', type: 'string', required: 'oui', description: 'Statut de la demande' },
                            { name: 'link', type: 'url', required: 'oui', description: 'Lien de paiement pour rediriger l\'utilisateur. L\'identifiant de la demande est le dernier segment de cette URL ; utilisez-le pour GET /transactions/requests/{id}' }
                        ],
                        curl: `curl -X POST "${getCliPaymentUrl()}" \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "amount": 100.5,\n    "currency": "USD",\n    "ref": "CMD-2024-001",\n    "referer_url": "https://votre-site.com/confirmation"\n  }'`
                    },
                    verificationById: {
                        title: 'Vérifier le statut d\'une demande',
                        description: 'Appelez GET /transactions/requests/{id} avec l\'identifiant de la demande : l\'id présent dans l\'URL du champ link (dernier segment).',
                        method: ENDPOINTS.paymentRequestById.method,
                        path: ENDPOINTS.paymentRequestById.path,
                        curl: `curl -X GET "${getPaymentRequestByIdUrl('<request_id>')}" \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Accept: application/json"`
                    }
                }
            }
        },
        {
            id: 'payment-verification',
            title: 'WebHooks',
            content: {
                description: `Pour être notifié en temps réel sur l'état de vos demandes de paiement, configurez votre URL webhook et choisissez les événements dont vous voulez être notifié. L'URL et la clé de signature se configurent via l'API Callback Destinations (authentification requise).`,
                webhookConfig: {
                    title: 'Configurer votre URL webhook (API Callback Destinations)',
                    basePath: 'Base URL : même que l\'API paiement. Préfixe : /api/v1/trs. Authentification : Bearer obligatoire.',
                    endpoints: [
                        { method: 'GET', path: '/callback-destinations', params: 'pge, lmt, org, cli, slg', description: 'Liste des destinations de callback pour une organisation et un client.' },
                        { method: 'GET', path: '/callback-destinations/{id}', description: 'Détail d\'une destination (endpoint, signing_key, events, etc.).' },
                        { method: 'POST', path: '/callback-destinations', description: 'Créer une nouvelle destination : endpoint (URL), companyId, clientId, events, description, enabled.' },
                        { method: 'PUT', path: '/callback-destinations/{id}', description: 'Modifier une destination existante.' },
                        { method: 'DELETE', path: '/callback-destinations/{id}', description: 'Supprimer une destination.' }
                    ],
                    requestBody: [
                        { name: 'endpoint', type: 'string', required: 'oui', description: 'URL qui recevra les webhooks (HTTPS recommandé)' },
                        { name: 'companyId', type: 'string', required: 'oui', description: 'Identifiant de l\'organisation' },
                        { name: 'clientId', type: 'string', required: 'oui', description: 'Identifiant du client / application' },
                        { name: 'events', type: 'string', required: 'oui', description: 'Événements à écouter (ex. payment.success,payment.failed ou liste séparée par des virgules)' },
                        { name: 'description', type: 'string', required: 'non', description: 'Description optionnelle' },
                        { name: 'enabled', type: 'boolean', required: 'non', description: 'Activer ou désactiver la destination (défaut true)' }
                    ],
                    responseNote: 'La réponse inclut signing_key : utilisez cette clé pour vérifier la signature (X-Signature) des requêtes reçues.'
                },
                webhookHeaders: STANDARD_HEADERS.webhook.map(h => ({
                    name: h.name,
                    type: h.value,
                    required: h.required ? 'oui' : 'non',
                    description: h.description
                })),
                webhookSignature: {
                    algorithm: 'HMAC-SHA256 sur le body brut de la requête (UTF-8), puis encodage Base64 du résultat.',
                    headersSent: 'X-Signature (signature Base64), X-Timestamp',
                    verifyExample: `// Node.js - Vérifier la signature du webhook
const crypto = require('crypto');
function verifyWebhookSignature(rawBody, signatureFromHeader, signingKey) {
  const expected = crypto.createHmac('sha256', signingKey).update(rawBody, 'utf8').digest('base64');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signatureFromHeader));
}
// Utilisez le body brut (string) reçu, pas un JSON re-sérialisé.`
                },
                requestBody: [
                    { name: 'event', type: 'string', required: 'oui', description: 'Type d\'événement' },
                    { name: 'ref', type: 'string', required: 'oui', description: 'Référence côté marchand' },
                    { name: 'trx', type: 'string', required: 'oui', description: 'ID de la demande de paiement' },
                    { name: 'status', type: 'string', required: 'oui', description: 'État de la demande' }
                ],
                eventTypes: [
                    { name: 'payment.success', type: 'La demande de paiement a été confirmée (transaction réussie).' },
                    { name: 'payment.cashed', type: 'Le montant en attente a été incrémenté dans la balance utilisable.' },
                    { name: 'payment.failed', type: 'La demande de paiement a échoué.' },
                    { name: 'payment.expired', type: 'Délai avant confirmation dépassé.' },
                    { name: 'payment.locked', type: 'Paiement bloqué (réclamation/anomalie) jusqu\'à compromis.' },
                    { name: 'payment.canceled', type: 'Paiement annulé avant confirmation.' }
                ],
                statuses: [
                    'requested : La demande est enregistrée et envoyée au réseau mobile du client',
                    'approved : La demande est confirmée par le client',
                    'completed : La balance utilisable est incrémentée (moins les frais)',
                    'failed : La demande a échoué (solde insuffisant ou problème technique)',
                    'expired : La demande a expiré (délai dépassé)',
                    'locked : Paiement bloqué (réclamation/anomalie)',
                    'canceled : Demande annulée par vous ou le client'
                ],
                examples: {
                    webhookPayload: `{\n  "event": "payment.success",\n  "trx": "txn_abc123",\n  "ref": "CMD-2024-001",\n  "status": "approved"\n}`
                }
            },
        },
        {
            id: 'rate-limits',
            title: 'Rate Limits',
            content: {
                description: `Les limites de taux sont gérées au niveau de l'infrastructure ou de la passerelle. En cas de dépassement, vous recevrez une réponse HTTP 429 (Too Many Requests). Les quotas précis par endpoint peuvent être communiqués par l'équipe Netikash ou documentés dans la passerelle.`,
                rateLimitsNote: true,
                headers: [
                    { name: 'X-RateLimit-Limit', description: 'Nombre maximum de requêtes autorisées (si renvoyé)' },
                    { name: 'X-RateLimit-Remaining', description: 'Nombre de requêtes restantes dans la fenêtre (si renvoyé)' },
                    { name: 'X-RateLimit-Reset', description: 'Timestamp de réinitialisation de la fenêtre (si renvoyé)' }
                ],
                errorResponse: {
                    status: 429,
                    body: {
                        error: 'rate_limit_exceeded',
                        message: 'Trop de requêtes. Réessayez plus tard.',
                        retry_after: 60
                    }
                }
            }
        },
        {
            id: 'error-codes-metier',
            title: 'Codes d\'erreur métier',
            content: {
                description: `En cas d'erreur de validation ou métier, l'API renvoie HTTP 200 avec un corps JSON contenant error: true, un message lisible et un code cible (target) pour le traitement programmatique. Utilisez target pour identifier l'erreur.`,
                errorResponseFormat: {
                    title: 'Format de la réponse d\'erreur',
                    fields: [
                        { name: 'message', type: 'string', description: 'Message d\'erreur lisible' },
                        { name: 'target', type: 'string', description: 'Code d\'erreur stable pour votre logique' },
                        { name: 'error', type: 'boolean', description: 'Toujours true en cas d\'erreur' },
                        { name: 'timestamp', type: 'string', description: 'Date/heure de l\'erreur (ZonedDateTime)' }
                    ],
                    example: `{\n  "message": "Le compte de destination est null",\n  "target": "receiver_id_error",\n  "error": true,\n  "timestamp": "2025-02-04T12:00:00Z"\n}`
                },
                errorCodes: [
                    { target: 'receiver_id_error', message: 'Le compte de destination est null' },
                    { target: 'solution_id_error', message: 'Compte de la solution inexistant ou solution_id vide' },
                    { target: 'extra_fees_error', message: 'Définir le frais de transaction (use_extra_fees sans extra_fees)' },
                    { target: 'token_error', message: 'Token invalide, manquant ou non associé à la transaction' },
                    { target: 'token_expired_Error', message: 'Ce token est déjà expiré' },
                    { target: 'status_error', message: 'Transaction déjà approuvée, complétée ou expirée' },
                    { target: 'request_error', message: 'Cette transaction n\'existe pas ou id requête manquant' },
                    { target: 'provider_id_error', message: 'Moyen de paiement non défini ou inexistant' },
                    { target: 'account_error', message: 'Numéro de compte vide ou format invalide' },
                    { target: 'control_error', message: 'Saisir le contrôle (provider avec with_control)' },
                    { target: 'full_name_error', message: 'Saisir le nom complet (provider avec with_full_name)' },
                    { target: 'suffix_error', message: 'Saisir le code suffixe du compte' },
                    { target: 'prefix_error', message: 'Saisir le code préfixe du compte' },
                    { target: 'expiration_date_error', message: 'Saisir la date d\'expiration' },
                    { target: 'empty_endpoint_error', message: 'Invalid endpoint (callback destination)' },
                    { target: 'empty_client_id_error', message: 'Invalid client id (callback destination)' },
                    { target: 'company_id_error', message: 'Aucune organisation trouvée avec cet id. Vérifiez votre payload et assurez-vous d\'utiliser uniquement l\'appel `cli-payments`.' },
                    { target: 'empty_company_id_error', message: 'Invalid company id (callback destination)' },
                    { target: 'empty_events_error', message: 'Invalid events (callback destination)' },
                    { target: 'callback_destination_exists', message: 'Callback destination already exists' },
                    { target: 'callback_destination_not_found', message: 'Callback destination not found' },
                    { target: 'currency_error', message: 'Devise est null ou référence vide' },
                    { target: 'amount_error', message: 'Le montant n\'est pas défini' },
                    { target: 'auth_error', message: 'Permission requise ou non autorisé' }
                ]
            }
        },
        {
            id: 'idempotency',
            title: 'Idempotency',
            content: {
                description: `Cette fonctionnalité n'est pas encore supportée par l'API. Nous vous recommandons d'éviter les doublons côté client (ex. référence unique par demande) jusqu'à sa disponibilité. Le comportement attendu lorsqu'elle sera disponible :`,
                idempotencyNotSupported: true,
                keyHeader: {
                    name: 'Idempotency-Key',
                    description: 'Clé unique pour identifier une requête idempotente',
                    format: 'UUID v4 ou chaîne aléatoire de 36 caractères'
                },
                behavior: [
                    'La première requête avec une clé d\'idempotence est traitée normalement',
                    'Les requêtes suivantes avec la même clé retournent la même réponse sans créer de nouvelle transaction',
                    'La clé est valide pendant 24 heures',
                    'Applicable uniquement aux endpoints POST (création de paiement)'
                ],
                example: {
                    curl: `curl -X POST "${getPaymentUrl()}" \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Content-Type: application/json" \\\n  -H "Idempotency-Key: 550e8400-e29b-41d4-a716-446655440000" \\\n  -d '{\n    "amount": 100.5,\n    "currency": "USD",\n    "ref": "CMD-2024-001",\n    "referer_url": "https://votre-site.com/confirmation"\n  }'`
                }
            }
        },
        {
            id: 'webhook-retries',
            title: 'Retries Webhooks',
            content: {
                description: `Netikash réessaie automatiquement l'envoi de webhooks en cas d'échec. Comprenez la stratégie de retry pour implémenter correctement votre endpoint.`,
                strategy: [
                    {
                        attempt: 1,
                        delay: 'Immédiat',
                        description: 'Premier envoi du webhook'
                    },
                    {
                        attempt: 2,
                        delay: '5 secondes',
                        description: 'Premier retry après échec'
                    },
                    {
                        attempt: 3,
                        delay: '30 secondes',
                        description: 'Deuxième retry'
                    },
                    {
                        attempt: 4,
                        delay: '5 minutes',
                        description: 'Troisième retry'
                    },
                    {
                        attempt: 5,
                        delay: '1 heure',
                        description: 'Dernier retry'
                    }
                ],
                timeout: 'Votre endpoint doit répondre dans les 10 secondes',
                bestPractices: [
                    'Répondez toujours avec un code HTTP 200 pour confirmer la réception',
                    'Validez la signature avant de traiter le webhook',
                    'Implémentez un mécanisme de déduplication basé sur l\'ID de transaction',
                    'Loggez tous les webhooks reçus pour le debugging'
                ]
            }
        },
        {
            id: 'http-codes',
            title: 'Codes HTTP',
            content: {
                description: `Référence complète des codes HTTP retournés par l'API Netikash.`,
                codes: [
                    {
                        code: 200,
                        name: 'OK',
                        description: 'Requête réussie',
                        examples: ['Récupération d\'un token', 'Vérification d\'un paiement']
                    },
                    {
                        code: 400,
                        name: 'Bad Request',
                        description: 'Requête mal formée ou paramètres invalides',
                        examples: ['JSON invalide', 'Paramètres manquants']
                    },
                    {
                        code: 401,
                        name: 'Unauthorized',
                        description: 'Authentification requise ou credentials invalides',
                        examples: ['Token manquant', 'Token expiré', 'Credentials incorrects']
                    },
                    {
                        code: 403,
                        name: 'Forbidden',
                        description: 'Accès refusé (permissions insuffisantes)',
                        examples: ['Scope insuffisant', 'Compte suspendu']
                    },
                    {
                        code: 404,
                        name: 'Not Found',
                        description: 'Ressource introuvable',
                        examples: ['Transaction inexistante', 'Endpoint invalide']
                    },
                    {
                        code: 422,
                        name: 'Unprocessable Entity',
                        description: 'Validation échouée',
                        examples: ['Montant invalide', 'Devise non supportée', 'Référence déjà utilisée']
                    },
                    {
                        code: 429,
                        name: 'Too Many Requests',
                        description: 'Limite de taux dépassée',
                        examples: ['Trop de requêtes par minute']
                    },
                    {
                        code: 500,
                        name: 'Internal Server Error',
                        description: 'Erreur serveur interne',
                        examples: ['Erreur temporaire', 'Problème de traitement']
                    },
                    {
                        code: 503,
                        name: 'Service Unavailable',
                        description: 'Service temporairement indisponible',
                        examples: ['Maintenance planifiée', 'Surcharge temporaire']
                    }
                ]
            }
        },

    ].filter((s) => s.id !== 'identifiants-comptes'), []);

    const sectionFromQuery = useMemo(() => {
        if (!q) return null;
        const qLower = q.toLowerCase();
        return (
            documentationSections.find((s) => s.id.toLowerCase() === qLower) ||
            documentationSections.find((s) => s.title.toLowerCase().includes(qLower)) ||
            documentationSections.find((s) => (s.content?.description || '').toLowerCase().includes(qLower)) ||
            null
        );
    }, [q, documentationSections]);

    useEffect(() => {
        if (sectionFromQuery?.id) {
            handleSectionChange(sectionFromQuery.id);
        } else if (sectionParam) {
            // Si section param existe, l'utiliser
            const found = documentationSections.find(s => s.id === sectionParam);
            if (found) setActiveSection(sectionParam);
        }
    }, [sectionFromQuery?.id, sectionParam, documentationSections, handleSectionChange]);

    const activeSectionData = documentationSections.find(section => section.id === activeSection);

    return (
        <>
            <Helmet>
                <title>Documentation API - Netikash Developer Portal</title>
                <meta name="description" content="Documentation complète de l'API Netikash : authentification OAuth2, création de paiements, webhooks, codes d'erreur et bonnes pratiques." />
                <meta name="keywords" content="Netikash API, documentation, OAuth2, paiement, webhook, intégration" />
            </Helmet>
        <div className="min-h-screen pt-16 bg-gradient-to-b from-gray-50 via-white to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 transition-colors">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
                {/* Header premium */}
                <div className="mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-200/60 bg-amber-50/70 text-amber-900 text-xs font-semibold tracking-wide dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
                        Documentation API
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500/80" />
                        Netikash Developer
                    </div>
                    <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                        Tout ce qu’il faut pour intégrer Netikash, proprement.
                    </h1>
                    <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-3xl">
                        Authentification OAuth2, demandes de paiement, webhooks, vérification, codes d’erreur et bonnes pratiques — avec des exemples et des paramètres détaillés.
                    </p>

                    {/* Search premium */}
                    <div className="mt-6 w-full max-w-3xl rounded-2xl border border-slate-200/90 bg-white shadow-[0_6px_30px_-18px_rgba(15,23,42,0.18)] px-4 py-3 flex items-center gap-3 focus-within:ring-2 focus-within:ring-amber-400/35 focus-within:border-amber-300/60 transition-all dark:border-slate-700/70 dark:bg-slate-900/70 dark:shadow-[0_10px_40px_-22px_rgba(0,0,0,0.55)]">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 dark:bg-amber-500/15 dark:text-amber-300">
                            <Search className="text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold dark:text-slate-400">
                                Recherche
                            </div>
                            <input
                                value={localQuery}
                                onChange={(e) => setLocalQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    const next = e.currentTarget.value.trim();
                                    if (!next) return;
                                    const nextLower = next.toLowerCase();
                                    const match =
                                        documentationSections.find((s) => s.id.toLowerCase() === nextLower) ||
                                        documentationSections.find((s) => s.title.toLowerCase().includes(nextLower)) ||
                                        documentationSections.find((s) => (s.content?.description || '').toLowerCase().includes(nextLower));
                                    if (match?.id) handleSectionChange(match.id);
                                }}
                                placeholder="Ex: OAuth2, cli-payments, webhook, X-Signature, erreurs…"
                                className="mt-1 w-full bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 outline-none border-0 p-0 dark:text-slate-100 dark:placeholder:text-slate-500"
                                aria-label="Rechercher dans la documentation"
                            />
                        </div>
                    </div>
                </div>

                {/* Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 items-start">
                    <div className="lg:sticky lg:top-24">
                        <SidebarNav
                            sections={documentationSections}
                            activeSection={activeSection}
                            onSectionChange={handleSectionChange}
                        />
                    </div>

                    <div className="min-w-0">
                        <div className="bg-white/90 backdrop-blur rounded-3xl border border-slate-200/90 shadow-[0_10px_40px_-26px_rgba(15,23,42,0.22)] overflow-hidden dark:bg-slate-900/60 dark:border-slate-700/70">
                            <ApiSection 
                                section={activeSectionData} 
                                sections={documentationSections}
                                activeSection={activeSection}
                                onSectionChange={handleSectionChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Documentation;